import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpClient
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, of } from 'rxjs';
import { catchError, filter, take, switchMap, finalize } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { ErrorReporterService } from '../services/error-reporter.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(private router: Router, private http: HttpClient, private errorReporter: ErrorReporterService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = sessionStorage.getItem('token');

    if (token) {
      request = this.addToken(request, token);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !request.url.includes('/auth/refresh') && !request.url.includes('/auth/login')) {
          return this.handle401Error(request, next);
        }

        if (error.status === 403) {
          if (error.error?.error === 'outside_working_hours') {
            const reason = error.error?.reason || 'outside_hours';
            this.router.navigate(['/blocked'], { queryParams: { reason } });
          } else if (error.error?.error === 'AVATAR_BLOCKED') {
            // Avatar accounts blocked from production — logout
            this.logout();
          }
          // else: permission denied (Insufficient permissions) — do NOT logout,
          // just let the error propagate so the page can show a proper message
          return throwError(() => error);
        }

        // Raporto te backend/Telegram: 5xx + gabime specifike 4xx
        if (error.status >= 500 || (error.status >= 400 && ![401,403,404].includes(error.status))) {
          this.errorReporter.reportHttpError(error.status, request.url, error.error);
        }

        return throwError(() => error);
      })
    );
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` }
    });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = sessionStorage.getItem('refreshToken');
      if (!refreshToken) {
        this.logout();
        return throwError(() => new HttpErrorResponse({ status: 401 }));
      }

      return this.http.post<any>(`${environment.apiUrl}/auth/refresh`, { refreshToken }).pipe(
        switchMap((response) => {
          if (response?.success && response?.data?.token) {
            const newToken = response.data.token;
            sessionStorage.setItem('token', newToken);
            this.refreshTokenSubject.next(newToken);
            return next.handle(this.addToken(request, newToken));
          }
          this.logout();
          return throwError(() => new HttpErrorResponse({ status: 401 }));
        }),
        catchError((err) => {
          this.logout();
          return throwError(() => err);
        }),
        finalize(() => {
          this.isRefreshing = false;
        })
      );
    } else {
      // Another request is already refreshing — wait for the new token
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next.handle(this.addToken(request, token!)))
      );
    }
  }

  private logout(): void {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('user');
    this.router.navigate(['/login']);
  }
}
